import { createOperationId } from '../../domain/operationId';
import { doc, getDocFromServer } from 'firebase/firestore';
import { firestorePaths } from '@qkindex/contracts';
import { db } from '../../firebase';
import { useFeedback } from '../feedback/FeedbackProvider';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  rapidCaptureDb,
  recordBytes,
  waitForCaptureSync,
} from './rapidCaptureDb';
import {
  processRapidCaptureRecord,
  rapidCaptureAuthority,
  applyCaptureStatus,
  editMutation,
} from './rapidCaptureOutbox';
import { captureService } from '../../services/captureService';
import { subscribeToCaptureStatus } from '../../repositories/captureStatusSubscription';
import {
  RapidCaptureAuthority,
  RapidCaptureDraft,
  RapidCaptureRecord,
  RapidCaptureStats,
  CaptureDraftFields,
  hasCaptureEdits,
  ItemEditOptions,
} from './rapidCaptureTypes';
import {
  discardRapidCaptureRecord,
  itemCreateMayHaveCommitted,
} from './rapidCaptureDiscard';
import { Item } from '../../domain/item';
import { shouldReconcileRapidCaptureRecord } from './rapidCaptureReconciliation';
import { getCollectionAiCapability } from '../../firebase/functions';
import { invalidateInventory } from '../collection/inventoryInvalidation';

export const RAPID_CAPTURE_UPLOAD_CONCURRENCY = 3;
const CAPABILITY_CACHE_MS = 120_000;
const emptyStats: RapidCaptureStats = {
  pending: 0,
  active: 0,
  analysisProcessing: 0,
  confirming: 0,
  failed: 0,
  total: 0,
  bytes: 0,
};
export interface RapidCaptureCompletion {
  items: number;
  labels: number;
}

/** The durable records are the queue. Slots discover new records after every wakeup,
 * rather than taking a snapshot that cannot accept work while uploads are running. */
export function useRapidCaptureOutbox(
  authority?: RapidCaptureAuthority,
  authoritativeItems: Item[] = [],
  collectionScoped = false,
) {
  const { notify } = useFeedback();
  const userId = authority?.userId;
  const [records, setRecords] = useState<RapidCaptureRecord[]>([]);
  const [stats, setStats] = useState<RapidCaptureStats>(emptyStats);
  const [online, setOnline] = useState(
    () => typeof navigator === 'undefined' || navigator.onLine,
  );
  const [completion, setCompletion] = useState<RapidCaptureCompletion | null>(
    null,
  );
  const epoch = authority
    ? `${authority.userId}:${authority.collectionId}:${authority.membershipVersion}:${authority.lifecycleVersion ?? 0}`
    : undefined;
  const epochRef = useRef(epoch);
  epochRef.current = epoch;
  const authorityRef = useRef(authority);
  authorityRef.current = authority;
  const itemsRef = useRef(authoritativeItems);
  itemsRef.current = authoritativeItems;
  const owner = useRef('');
  if (!owner.current) owner.current = createOperationId();
  const running = useRef(new Map<string, RapidCaptureRecord>());
  const admissions = useRef(new Map<string, RapidCaptureRecord>());
  const stoppedWorkers = useRef(new Set<string>());
  const editors = useRef(new Map<string, RapidCaptureRecord>());
  const scanning = useRef(false);
  const scanRequested = useRef(false);
  const processRef = useRef<() => Promise<void>>(async () => undefined);
  const durableStorageAvailable = typeof indexedDB !== 'undefined';
  useEffect(() => {
    epochRef.current = epoch;
    return () => {
      if (epochRef.current === epoch) epochRef.current = undefined;
    };
  }, [epoch]);
  const capability = useRef<
    | { epoch: string; version: string; remaining: number; expires: number }
    | undefined
  >(undefined);
  const capabilityRequest = useRef<
    { epoch: string; value: Promise<string> } | undefined
  >(undefined);

  const cachedAiCapability = useCallback(async (): Promise<string> => {
    const current = authorityRef.current;
    if (!current || !epoch)
      throw new Error('Collection access is unavailable.');
    const cached = capability.current;
    if (
      cached?.epoch === epoch &&
      cached.expires > Date.now() &&
      cached.remaining > 3
    ) {
      cached.remaining -= 1;
      return cached.version;
    }
    if (capabilityRequest.current?.epoch === epoch)
      return capabilityRequest.current.value;
    const value = getCollectionAiCapability({
      collectionId: current.collectionId,
      expectedMembershipVersion: current.membershipVersion,
      workflow: 'image-analysis',
      feature: 'image-analysis',
      imageCount: 1,
    }).then(({ data }) => {
      if (epochRef.current !== epoch || !data.allowed)
        throw new Error('AI admission is unavailable.');
      capability.current = {
        epoch,
        version: data.costVersion,
        remaining: (data.headroom ?? 1) - 1,
        expires: Date.now() + CAPABILITY_CACHE_MS,
      };
      return data.costVersion;
    });
    const request = { epoch, value };
    capabilityRequest.current = request;
    try {
      return await value;
    } finally {
      if (capabilityRequest.current === request)
        capabilityRequest.current = undefined;
    }
  }, [epoch]);
  const prefetchAiCapability = useCallback(() => {
    if (navigator.onLine) void cachedAiCapability().catch(() => undefined);
  }, [cachedAiCapability]);

  const refresh = useCallback(async () => {
    if (!userId || !durableStorageAvailable) {
      setRecords([]);
      setStats(emptyStats);
      return;
    }
    const currentEpoch = epoch;
    try {
      const all = await rapidCaptureDb.list(userId);
      if (epochRef.current !== currentEpoch) return;
      const currentAuthority = authorityRef.current;
      const visible = all.filter(
        (record) =>
          record.collectionId === currentAuthority?.collectionId &&
          (record.state === 'terminal' ||
            (record.membershipVersion === currentAuthority?.membershipVersion &&
              record.lifecycleVersion === currentAuthority?.lifecycleVersion)),
      );
      setRecords(visible);
      setStats({
        pending: visible.filter((r) => r.state === 'pending').length,
        active: visible.filter((r) => r.state === 'submitting').length,
        analysisProcessing: visible.filter(
          (r) => r.state === 'analysis-submitting',
        ).length,
        confirming: visible.filter((r) => r.state === 'confirming').length,
        failed: visible.filter(
          (r) => r.state === 'failed' || r.state === 'terminal',
        ).length,
        total: visible.length,
        bytes: visible.reduce(
          (bytes, record) => bytes + recordBytes(record),
          0,
        ),
      });
    } catch {
      /* Keep the last observed drafts visible on transient local-storage failure. */
    }
  }, [durableStorageAvailable, epoch, userId]);

  const process = useCallback(async () => {
    const authority = authorityRef.current;
    if (
      !userId ||
      !authority ||
      !durableStorageAvailable ||
      !online ||
      epochRef.current !== epoch
    )
      return;
    scanRequested.current = true;
    if (scanning.current) return;
    scanning.current = true;
    try {
      do {
        scanRequested.current = false;
        const queued = await rapidCaptureDb.list(userId);
        if (epochRef.current !== epoch) break;
        for (const record of queued) {
          if (record.collectionId !== authority.collectionId) continue;
          if (record.membershipVersion !== authority.membershipVersion) {
            await rapidCaptureDb.purgeCollectionAuthority(
              userId,
              record.collectionId,
              authority.membershipVersion,
              'membership-changed',
            );
            continue;
          }
          if (record.lifecycleVersion !== authority.lifecycleVersion) {
            await rapidCaptureDb.terminalizeCollectionLifecycle(
              userId,
              record.collectionId,
              authority.lifecycleVersion ?? 0,
            );
            continue;
          }
          if (
            running.current.has(record.operationId) ||
            admissions.current.has(record.operationId) ||
            stoppedWorkers.current.has(record.operationId) ||
            record.itemUnavailable ||
            record.state === 'terminal' ||
            (record.nextAttemptAt &&
              Date.parse(record.nextAttemptAt) > Date.now()) ||
            (record.leaseOwner !== owner.current &&
              (record.leaseExpiresAt ?? 0) > Date.now())
          )
            continue;
          const editsReady =
            record.itemCreated &&
            hasCaptureEdits(record) &&
            !(record.state === 'failed' && record.lastError?.phase === 'edits');
          const analysisReady =
            record.itemCreated &&
            record.analyzeImage &&
            !record.aiStarted &&
            !record.aiDismissed &&
            record.state !== 'failed';
          if (
            !record.controlAction &&
            !editsReady &&
            !analysisReady &&
            (record.itemCreated || record.state === 'failed')
          )
            continue;
          const work =
            record.itemCreated && !editsReady
              ? admissions.current
              : running.current;
          const limit =
            work === admissions.current ? 2 : RAPID_CAPTURE_UPLOAD_CONCURRENCY;
          if (work.size >= limit) continue;
          const claimed = await rapidCaptureDb.claim(record, owner.current);
          if (!claimed || epochRef.current !== epoch) continue;
          work.set(record.operationId, claimed);
          const active = () => epochRef.current === epoch;
          void processRapidCaptureRecord(
            claimed,
            active,
            userId,
            cachedAiCapability,
          )
            .catch(async () => {
              // A storage exception outside the handled workflow must not create
              // an infinite microtask retry loop on an unchanged queue record.
              stoppedWorkers.current.add(record.operationId);
              await rapidCaptureDb
                .update(record, (current) => ({
                  ...current,
                  state: 'failed',
                  lastError: {
                    phase: current.itemCreated ? 'edits' : 'upload',
                    category: 'unknown',
                    message:
                      'Local synchronization failed. Retry to resume; the draft is retained.',
                  },
                }))
                .catch(() => undefined);
            })
            .finally(async () => {
              await rapidCaptureDb
                .release(claimed, owner.current)
                .catch(() => undefined);
              work.delete(record.operationId);
              if (active()) {
                await refresh();
                void processRef.current();
              }
            });
        }
      } while (
        scanRequested.current &&
        epochRef.current === epoch &&
        (running.current.size < RAPID_CAPTURE_UPLOAD_CONCURRENCY ||
          admissions.current.size < 2)
      );
    } finally {
      scanning.current = false;
    }
  }, [
    cachedAiCapability,
    durableStorageAvailable,
    epoch,
    online,
    refresh,
    userId,
  ]);
  processRef.current = process;

  const enqueue = useCallback(
    async (draft: RapidCaptureDraft) => {
      if (!authority) throw new Error('Sign in before capturing items.');
      if (!durableStorageAvailable)
        throw new Error(
          'Durable browser storage is unavailable. Use Add Item instead.',
        );
      // Optional capability lookup runs in the worker, never on the shutter path.
      const record = await rapidCaptureDb.enqueue(authority, draft);
      if (epochRef.current !== epoch)
        throw new Error('Collection access changed while capturing.');
      await refresh();
      void processRef.current();
      return record;
    },
    [authority, durableStorageAvailable, epoch, refresh],
  );

  const holdDraft = useCallback(
    async (operationId: string, hold: boolean) => {
      const record = await rapidCaptureDb.get(operationId);
      if (
        !record ||
        record.userId !== userId ||
        record.collectionId !== authority?.collectionId ||
        epochRef.current !== epoch
      )
        throw new Error('This local draft is no longer available.');
      if (hold) editors.current.set(operationId, record);
      else editors.current.delete(operationId);
      await rapidCaptureDb.holdDraft(record, owner.current, hold);
      await refresh();
    },
    [authority?.collectionId, epoch, refresh, userId],
  );

  const enqueueEdit = useCallback(
    async (
      item: Item,
      fields: Partial<CaptureDraftFields>,
      image?: Blob | null,
      options: ItemEditOptions = {},
    ) => {
      if (!authority || !durableStorageAvailable)
        throw new Error('Durable item editing is unavailable.');
      const existing = (await rapidCaptureDb.list(authority.userId)).find(
        (record) =>
          record.schemaVersion === 6 &&
          record.collectionId === authority.collectionId &&
          record.itemId === item.id &&
          record.membershipVersion === authority.membershipVersion &&
          record.lifecycleVersion === authority.lifecycleVersion &&
          record.state !== 'terminal',
      );
      if (
        existing?.lastError?.category === 'conflict' &&
        options.reviewedRevision !== undefined
      ) {
        if (!online)
          throw new Error('Reconnect before resolving this conflict.');
        const pending = existing.editSnapshot
          ? editMutation(existing, existing.editSnapshot)
          : existing.mutation && !existing.itemCreated
            ? existing
            : undefined;
        if (pending) {
          await captureService.control(pending, 'discard');
        }
      }
      if (epochRef.current !== epoch)
        throw new Error('Collection access changed while saving.');
      const saved = await rapidCaptureDb.enqueueEdit(
        authority,
        item,
        fields,
        image,
        options,
      );
      if (epochRef.current !== epoch)
        throw new Error('Collection access changed while saving.');
      stoppedWorkers.current.delete(saved.operationId);
      await refresh();
      void processRef.current();
      if (options.awaitConfirmation) {
        await holdDraft(saved.operationId, true);
        try {
          if (!online)
            throw new Error(
              'The changes are saved on this device. Reconnect before moving the item.',
            );
          await waitForCaptureSync(saved);
          if (epochRef.current !== epoch)
            throw new Error('Collection access changed while saving.');
        } finally {
          await holdDraft(saved.operationId, false).catch(() => undefined);
        }
      }
      return saved;
    },
    [authority, durableStorageAvailable, epoch, online, refresh, holdDraft],
  );

  const retry = useCallback(
    async (operationId: string) => {
      const authority = authorityRef.current;
      const record = await rapidCaptureDb.get(operationId);
      stoppedWorkers.current.delete(operationId);
      if (
        !record ||
        record.userId !== userId ||
        !authority ||
        record.collectionId !== authority.collectionId ||
        record.state === 'terminal'
      )
        return;
      if (record.schemaVersion !== 6) {
        if (
          !online ||
          authority.membershipVersion !== record.membershipVersion ||
          authority.lifecycleVersion !== record.lifecycleVersion
        )
          return;
        try {
          const item = await getDocFromServer(
            doc(db, firestorePaths.item(record.collectionId, record.itemId)),
          );
          if (epochRef.current !== epoch) return;
          if (item.exists()) {
            if (item.data().captureOperationId !== record.operationId)
              throw new Error(
                'Check the saved item before discarding this older photo.',
              );
          } else {
            if (
              record.itemCreated ||
              (record.analyzeImage &&
                (record.serverAccepted || record.itemCreateAttempted))
            )
              throw new Error(
                'This older capture may have paid AI work. Its photo is retained for explicit reconciliation.',
              );
            await rapidCaptureDb.enqueue(
              authority,
              {
                image: record.image,
                keywords: record.keywords,
                location: record.location,
                locationId: record.locationId,
                qty: record.qty,
                tagIds: record.tagIds,
              },
              undefined,
              record.analyzeImage ? 'cost-unconfirmed' : undefined,
              record.itemId,
            );
          }
          await rapidCaptureDb.remove(operationId);
          invalidateInventory({
            userId: record.userId,
            collectionId: record.collectionId,
            resources: ['items'],
            reloadItemIds: [record.itemId],
          });
        } catch (error) {
          notify(
            error instanceof Error
              ? error.message
              : 'The older photo could not be recovered.',
            'warning',
          );
        }
        await refresh();
        void processRef.current();
        return;
      }
      if (
        record.lastError?.phase === 'edits' &&
        record.lastError.category === 'conflict'
      )
        throw new Error(
          'Open Edit details to review and resolve this conflict.',
        );
      let analysisOperationId = record.analysisOperationId;
      let acceptedAiCostVersion = record.acceptedAiCostVersion;
      if (
        record.itemCreated &&
        record.analyzeImage &&
        (record.aiSettlement === 'refunded' ||
          record.aiSkipReason === 'cost-changed')
      ) {
        const result = (
          await getCollectionAiCapability({
            collectionId: record.collectionId,
            expectedMembershipVersion: record.membershipVersion,
            workflow: 'image-analysis',
            feature: 'image-analysis',
            imageCount: 1,
          })
        ).data;
        if (epochRef.current !== epoch || !result.allowed) return;
        if (
          record.aiSkipReason === 'cost-changed' &&
          record.pendingAiCostVersion !== result.costVersion
        ) {
          await rapidCaptureDb.update(record, (current) => ({
            ...current,
            pendingAiCostVersion: result.costVersion,
            pendingAiCredits: result.credits,
          }));
          return;
        }
        acceptedAiCostVersion = result.costVersion;
        if (record.aiSettlement === 'refunded')
          analysisOperationId = createOperationId();
      }
      await rapidCaptureDb.update(record, (current) => ({
        ...current,
        controlAction:
          current.itemCreated &&
          current.aiStarted &&
          current.lastError?.phase !== 'edits'
            ? 'retry'
            : current.controlAction,
        state: current.itemCreated
          ? current.completion
            ? 'confirming'
            : 'analysis-submitting'
          : 'pending',
        analysisOperationId,
        acceptedAiCostVersion,
        lastError: undefined,
        attempts: current.itemCreated ? current.attempts : 0,
        editAttempts: 0,
        controlAttempts: 0,
        aiSettlement: undefined,
        aiSkipReason: undefined,
        pendingAiCostVersion: undefined,
        pendingAiCredits: undefined,
        nextAttemptAt: undefined,
      }));
      await refresh();
      void processRef.current();
    },
    [epoch, notify, online, refresh, userId],
  );

  const dismissAI = useCallback(
    async (operationId: string) => {
      const record = await rapidCaptureDb.get(operationId);
      if (
        !record ||
        record.userId !== userId ||
        record.schemaVersion !== 6 ||
        !record.itemCreated
      )
        return;
      const dismissing = await rapidCaptureDb.update(record, (current) => ({
        ...current,
        aiDismissed: true,
        controlAction: 'save',
        state: 'analysis-submitting',
        lastError: undefined,
        nextAttemptAt: undefined,
      }));
      if (dismissing && online) {
        try {
          const receipt = await captureService.control(dismissing, 'save');
          if (epochRef.current === epoch)
            await applyCaptureStatus(dismissing, receipt, 'save');
        } catch {
          /* Durable intent remains for an exact worker retry. */
        }
      }
      void processRef.current();
    },
    [epoch, online, userId],
  );

  const discard = useCallback(
    async (operationId: string) => {
      const queued = await rapidCaptureDb.get(operationId);
      if (!queued || queued.userId !== userId) return;
      const actionOwner = `action:${owner.current}`;
      const record = await rapidCaptureDb.claim(queued, actionOwner);
      if (!record)
        throw new Error(
          'These changes are still syncing. Try discarding after the current save finishes.',
        );
      try {
        if (record.schemaVersion === 6) rapidCaptureAuthority(record);
        if (record.itemCreated && hasCaptureEdits(record)) {
          if (record.editSnapshot)
            await captureService.control(
              editMutation(record, record.editSnapshot),
              'discard',
            );
          await rapidCaptureDb.update(record, (current) => ({
            ...current,
            edits: undefined,
            editSnapshot: undefined,
            pendingImage: undefined,
            pendingImageRevision: undefined,
            localRevision: current.syncedRevision,
            lastError:
              current.lastError?.phase === 'ai' ? current.lastError : undefined,
            state:
              current.lastError?.phase === 'ai'
                ? current.state
                : current.completion
                  ? 'confirming'
                  : 'analysis-submitting',
            nextAttemptAt: undefined,
          }));
          await refresh();
          return;
        }
        await discardRapidCaptureRecord(record, {
          removeRecord: (id) => rapidCaptureDb.remove(id),
        });
        if (itemCreateMayHaveCommitted(record))
          invalidateInventory({
            userId: record.userId,
            collectionId: record.collectionId,
            resources: ['items'],
            reloadItemIds: [record.itemId],
          });
        await refresh();
      } finally {
        await rapidCaptureDb.release(record, actionOwner);
      }
    },
    [refresh, userId],
  );

  const markItemUnavailable = useCallback(
    async (operationId: string) => {
      const record = await rapidCaptureDb.get(operationId);
      if (
        !record ||
        (!record.itemCreated && !record.mutation) ||
        record.userId !== userId ||
        record.collectionId !== authorityRef.current?.collectionId ||
        epochRef.current !== epoch
      )
        return;
      await rapidCaptureDb.update(record, (current) =>
        current.itemUnavailable
          ? current
          : {
              ...current,
              itemUnavailable: true,
              state: 'failed',
              nextAttemptAt: undefined,
              lastError: {
                phase: 'edits',
                category: 'conflict',
                message:
                  'The saved item was deleted or moved. Local changes are retained; dismiss this activity when you have reviewed them.',
              },
            },
      );
      await refresh();
    },
    [epoch, refresh, userId],
  );

  const reconcile = useCallback(async () => {
    if (!userId || !durableStorageAvailable) return;
    const queued = await rapidCaptureDb.list(userId);
    for (const record of queued) {
      if (
        epochRef.current !== epoch ||
        record.collectionId !== authority?.collectionId
      )
        continue;
      const item = itemsRef.current.find(
        (candidate) =>
          candidate.collectionId === record.collectionId &&
          candidate.id === record.itemId,
      );
      if (!shouldReconcileRapidCaptureRecord(record, item)) continue;
      if (await rapidCaptureDb.removeCompleted(record))
        setCompletion({
          items: 1,
          labels: record.completion === 'analysis-complete' ? 1 : 0,
        });
    }
  }, [authority?.collectionId, durableStorageAvailable, epoch, userId]);

  useEffect(() => {
    if (!userId || !durableStorageAvailable) {
      setRecords([]);
      setStats(emptyStats);
      setCompletion(null);
      return;
    }
    const activeRecords = running.current;
    const activeAdmissions = admissions.current;
    const openEditors = editors.current;
    const leaseOwner = owner.current;
    const wake = () => {
      void refresh();
      void processRef.current();
    };
    const unsubscribe = rapidCaptureDb.subscribe(wake);
    void rapidCaptureDb
      .resetForSession(
        userId,
        collectionScoped ? authority?.collectionId : undefined,
      )
      .then(wake)
      .catch(() => undefined);
    const resume = () => {
      setOnline(navigator.onLine);
      if (navigator.onLine) wake();
    };
    const offline = () => setOnline(false);
    window.addEventListener('online', resume);
    window.addEventListener('offline', offline);
    document.addEventListener('visibilitychange', resume);
    return () => {
      unsubscribe();
      window.removeEventListener('online', resume);
      window.removeEventListener('offline', offline);
      document.removeEventListener('visibilitychange', resume);
      activeRecords.forEach((record) => {
        void rapidCaptureDb.release(record, leaseOwner);
      });
      activeAdmissions.forEach((record) => {
        void rapidCaptureDb.release(record, leaseOwner);
      });
      openEditors.forEach((record) => {
        void rapidCaptureDb.holdDraft(record, leaseOwner, false);
      });
      openEditors.clear();
    };
  }, [
    authority?.collectionId,
    collectionScoped,
    durableStorageAvailable,
    refresh,
    userId,
  ]);

  useEffect(() => {
    void process();
  }, [process]);
  useEffect(() => {
    void reconcile().catch(() => undefined);
  }, [authoritativeItems, reconcile, records]);
  useEffect(() => {
    const deadlines = records
      .flatMap((record) => [
        record.nextAttemptAt ? Date.parse(record.nextAttemptAt) : 0,
        record.leaseExpiresAt ?? 0,
        ...Object.values(record.editorLeases ?? {}),
      ])
      .filter((time) => time > Date.now());
    if (!deadlines.length) return;
    const timer = window.setTimeout(
      () => {
        void refresh();
        void processRef.current();
      },
      Math.min(
        2_147_483_647,
        Math.max(1, Math.min(...deadlines) - Date.now() + 1),
      ),
    );
    return () => window.clearTimeout(timer);
  }, [records, refresh]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      running.current.forEach((record) => {
        void rapidCaptureDb.claim(record, owner.current);
      });
      admissions.current.forEach((record) => {
        void rapidCaptureDb.claim(record, owner.current);
      });
      editors.current.forEach((record) => {
        void rapidCaptureDb.holdDraft(record, owner.current, true);
      });
    }, 20_000);
    return () => window.clearInterval(timer);
  }, []);

  const subscriptions = useRef(
    new Map<string, { epoch: string; stop: () => void }>(),
  );
  useEffect(() => {
    const active = subscriptions.current;
    const wanted = records.filter(
      (record) =>
        online &&
        record.schemaVersion === 6 &&
        record.serverAccepted &&
        record.itemCreated &&
        !record.controlAction &&
        !record.completion &&
        record.state !== 'terminal' &&
        record.state !== 'failed' &&
        record.collectionId === authority?.collectionId &&
        record.membershipVersion === authority.membershipVersion &&
        record.lifecycleVersion === authority.lifecycleVersion,
    );
    active.forEach((entry, id) => {
      if (
        entry.epoch !== epoch ||
        !wanted.some((record) => record.operationId === id)
      ) {
        entry.stop();
        active.delete(id);
      }
    });
    wanted.forEach((record) => {
      if (!epoch || active.has(record.operationId)) return;
      let attached = true;
      const stop = subscribeToCaptureStatus(
        record,
        (receipt) => {
          if (attached && epochRef.current === epoch)
            void applyCaptureStatus(record, receipt);
        },
        () => {
          if (!attached || epochRef.current !== epoch) return;
          void rapidCaptureDb.update(record, (current) => ({
            ...current,
            state: 'failed',
            lastError: {
              phase: 'ai',
              category: 'network',
              message:
                'Live updates are unavailable. Retry to reconnect; the saved item is retained.',
            },
          }));
        },
      );
      active.set(record.operationId, {
        epoch,
        stop: () => {
          attached = false;
          stop();
        },
      });
    });
  }, [
    authority?.collectionId,
    authority?.membershipVersion,
    authority?.lifecycleVersion,
    epoch,
    online,
    records,
  ]);
  useEffect(() => {
    const current = subscriptions.current;
    return () => {
      current.forEach((entry) => entry.stop());
      current.clear();
    };
  }, [epoch]);

  return {
    available: durableStorageAvailable,
    records,
    stats,
    online,
    completion,
    clearCompletion: () => setCompletion(null),
    enqueue,
    prefetchAiCapability,
    retry,
    dismissAI,
    discard,
    refresh,
    process,
    reconcile,
    enqueueEdit,
    holdDraft,
    markItemUnavailable,
  };
}
